import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { learnerService } from '../../services/api';

const Star = ({ filled, onClick, disabled }) => (
  <Box onClick={disabled ? null : onClick} sx={{ cursor: disabled ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center' }}>
    {filled ? <StarIcon color="warning" /> : <StarBorderIcon />}
  </Box>
);

export default function ContentRatingModal({ open, onClose, contentId, onSubmitted }) {
  const [loading, setLoading] = useState(false);
  const [average, setAverage] = useState(0);
  const [totalRatings, setTotalRatings] = useState(0);
  const [userRating, setUserRating] = useState(null); // {id, rating, comment}
  const [selectedRating, setSelectedRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !contentId) return;
    setLoading(true);
    learnerService.getContentRatings(contentId)
      .then(res => {
        if (res.data && res.data.success) {
          const d = res.data.data;
          setAverage(Number(d.average_rating) || 0);
          setTotalRatings(d.total_ratings || 0);
          setUserRating(d.user_rating || null);
          if (d.user_rating) {
            setSelectedRating(d.user_rating.rating);
            setComment(d.user_rating.comment || '');
          } else {
            setSelectedRating(0);
            setComment('');
          }
        }
      })
      .catch(err => {
        console.error(err);
      })
      .finally(() => setLoading(false));
  }, [open, contentId]);

  const onStarClick = (value) => {
    if (userRating) return; // no edit allowed
    setSelectedRating(value);
  };

  const handleSubmit = async () => {
    if (!selectedRating || selectedRating < 1) {
      alert('Please choose a rating between 1 and 5');
      return;
    }
    setSubmitting(true);
    try {
      const res = await learnerService.submitContentRating(contentId, selectedRating, comment);
      if (res.data && res.data.success) {
        // update local UI
        setAverage(res.data.data.average_rating || average);
        setTotalRatings(res.data.data.total_ratings || totalRatings);
        // mark that user has rated to disable editing
        setUserRating({
          id: res.data.data.rating_id,
          rating: selectedRating,
          comment: comment || null,
          created_at: new Date().toISOString()
        });
        if (onSubmitted) onSubmitted();
      } else {
        alert(res.data?.message || 'Failed to save rating');
      }
    } catch (err) {
      console.error('submitContentRating error', err);
      alert(err.response?.data?.message || 'Server error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Ratings & Comments</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box mb={2}>
          <Typography variant="subtitle2">Average rating</Typography>
          <Box display="flex" alignItems="center">
            {[1,2,3,4,5].map(i => <Star key={i} filled={i <= Math.round(average)} disabled />)}
            <Box ml={1}>
              <Typography variant="body2">{average} ({totalRatings} ratings)</Typography>
            </Box>
          </Box>
        </Box>

        {userRating ? (
          <Box>
            <Typography variant="subtitle2">Your rating</Typography>
            <Box display="flex" alignItems="center" mb={1}>
              {[1,2,3,4,5].map(i => <Star key={i} filled={i <= userRating.rating} disabled />)}
              <Box ml={1}>
                <Typography variant="body2">Submitted on {new Date(userRating.created_at).toLocaleString()}</Typography>
              </Box>
            </Box>
            {userRating.comment ? (
              <Box>
                <Typography variant="subtitle2">Your comment</Typography>
                <Typography variant="body2">{userRating.comment}</Typography>
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary">No comment provided.</Typography>
            )}
          </Box>
        ) : (
          <Box>
            <Typography variant="subtitle2">Add your rating</Typography>
            <Box display="flex" alignItems="center" mb={1}>
              {[1,2,3,4,5].map(i => (
                <Box key={i} onClick={() => onStarClick(i)} sx={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}>
                  {i <= selectedRating ? <StarIcon color="warning" /> : <StarBorderIcon />}
                </Box>
              ))}
            </Box>
            <TextField
              label="Optional comment"
              fullWidth
              multiline
              minRows={2}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Share your feedback (optional)"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {!userRating && (
          <Button onClick={handleSubmit} disabled={submitting} variant="contained">
            Submit
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}